import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 型定義 ---

/** プロファイル種別。AUTO=自動保存（画面状態）、MANUAL=ユーザー任意保存（将来用） */
export type ProfileType = 'AUTO' | 'MANUAL';

export type UserProfileResult = {
  userProfileId: string;
  profileKey: string;
  profileType: string;
  profileName: string;
  profileValue: string;
};

export type SaveUserProfileParams = {
  profileKey: string;
  profileValue: string;
};

export type CreateUserProfileParams = {
  profileKey: string;
  profileName: string;
  profileValue: string;
};

export type DeleteUserProfileParams = {
  userProfileId: string;
};

// --- API関数 ---

/**
 * ユーザープロファイル取得API（API8201）
 * @param profileKey - プロファイルキー（必須, 最大100文字, 画面・用途の識別）
 * @param profileType - プロファイル種別（任意, enum: AUTO | MANUAL）。AUTO 復元時は 'AUTO' を指定
 * @returns results 配列。0件でも空配列を返す
 */
export const listUserProfile = async (
  profileKey: string,
  profileType?: ProfileType,
): Promise<UserProfileResult[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.get(`${KanzhongBackend.GET_USER_PROFILE}/${profileKey}`, {
      headers: {
        'X-Session-Id': sessionId,
      },
      params: profileType ? { profileType } : undefined,
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ユーザープロファイル保存API（API8204・AUTO upsert）
 * @param params.profileKey - プロファイルキー（必須, 最大100文字）
 * @param params.profileValue - プロファイル値（必須, 画面状態のJSON文字列）
 * @returns userProfileId
 */
export const saveUserProfile = async (params: SaveUserProfileParams): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.SAVE_USER_PROFILE, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.userProfileId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ユーザープロファイル登録API（API8202・MANUAL専用・将来用）
 * @param params.profileKey - プロファイルキー（必須, 最大100文字）
 * @param params.profileName - プロファイル名（必須, 最大50文字）
 * @param params.profileValue - プロファイル値（必須, 画面状態のJSON文字列）
 * @returns userProfileId
 */
export const createUserProfile = async (params: CreateUserProfileParams): Promise<string> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.CREATE_USER_PROFILE, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.userProfileId;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * ユーザープロファイル削除API（API8203）
 * @param params.userProfileId - ユーザープロファイルID（必須, 最大64文字, UUID）
 */
export const deleteUserProfile = async (params: DeleteUserProfileParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.DELETE_USER_PROFILE, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
